import { MenuItem, MenuList, Paper, Popper, Typography } from "@mui/material";
import * as React from "react";

import { MirrorHighlightTextarea } from "@/shared/components";
import {
  activeTopicTokenAtCursor,
  findTopicReferences,
  splitTopicQuery,
} from "@/shared/domain/skills/topicReference";
import { useTopicFields } from "@/shared/domain/topics/hooks";
import { useCaretAutocomplete, useComboboxIds } from "@/shared/hooks";

import styles from "./TopicBodyEditor.module.css";

/** Maximum suggestions shown in the autocomplete menu. */
const MAX_SUGGESTIONS = 8;

interface Props {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /** Org topic names offered after a bare `/` and highlighted when referenced. */
  topicNames: readonly string[];
  /** Owning org, used to fetch a referenced topic's fields for `/topic.` drill-in. */
  orgId: string | undefined;
  helperText?: string;
  minRows?: number;
  required?: boolean;
  /** When true, the editor flexes to fill its parent's height (rather than a
   * fixed `minRows` height) — used to give the body most of a tall dialog. */
  grow?: boolean;
  /** Extra className applied to both text layers (e.g. the monospace body font). */
  inputClassName?: string;
}

interface Suggestion {
  /** Menu label. */
  label: string;
  /** Full token (including leading `/`) placed from ``slashStart``. */
  insertText: string;
  /** Secondary label shown muted to the right (e.g. "topic" / "field"). */
  kind: string;
}

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

/**
 * Multiline skill-body editor with a `/` autocomplete over the org's topics and
 * live highlighting of resolved topic references.
 *
 * The visible surface is a mirror ``<div>`` rendered behind a transparent-text
 * ``<textarea>``: the caret and editing come from the textarea, the coloring
 * from the overlay, whose fonts and padding match exactly so the two stay in
 * lockstep. Tokens that resolve to a known org topic (via
 * {@link findTopicReferences}) are wrapped in a highlight span — the same
 * references the skill persists as ``relevant_topics`` and the chat AI
 * pre-loads schemas for.
 *
 * Typing `/` opens a menu of topic names; once a complete known topic is
 * followed by a `.`, the menu switches to that topic's own fields (fetched by
 * name from the shared-topics/fields schema), not the org-wide catalog. Only
 * the topic name is persisted; the field suffix is authoring prose.
 */
export function TopicBodyEditor({
  label,
  value,
  onChange,
  topicNames,
  orgId,
  helperText,
  minRows = 6,
  required,
  grow,
  inputClassName,
}: Props) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = React.useState(false);

  // Shared caret-autocomplete engine (the `/`-menu state machine, overlay
  // scroll sync, keyboard nav, and token-replacing accept). This component
  // supplies the topic-token detector and its own suggestion + overlay
  // rendering below.
  const {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    anchorEl,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  } = useCaretAutocomplete({
    onChange,
    textareaRef,
    overlayRef,
    paperRef,
    detectToken: activeTopicTokenAtCursor,
  });

  const labelId = React.useId();
  const { listboxId, optionId } = useComboboxIds();

  // Split the body into plain / highlighted segments for the overlay. Rebuilt
  // only when the text or the topic catalog changes. A reference emits a topic
  // chip and, when a `.field` path follows, a separate field chip; the
  // connecting `.` is left plain so the two read as clearly distinct but
  // related tokens rather than one fused blob.
  const segments = React.useMemo(() => {
    const matches = findTopicReferences(value, topicNames);
    const parts: Array<{
      text: string;
      kind: "plain" | "topic" | "topicUnknown" | "field";
    }> = [];
    let cursor = 0;
    for (const match of matches) {
      if (match.start > cursor) {
        parts.push({ text: value.slice(cursor, match.start), kind: "plain" });
      }
      parts.push({
        text: value.slice(match.start, match.end),
        // Known topics get the solid chip; a reference to a topic not (yet) in
        // the org catalog is styled distinctly so the author sees it is treated
        // as a topic that just isn't present yet.
        kind: match.known ? "topic" : "topicUnknown",
      });
      if (match.fieldEnd > match.end) {
        // Separator dot stays plain; the field path (after it) is its own chip.
        parts.push({
          text: value.slice(match.end, match.end + 1),
          kind: "plain",
        });
        parts.push({
          text: value.slice(match.end + 1, match.fieldEnd),
          kind: "field",
        });
      }
      cursor = match.fieldEnd;
    }
    if (cursor < value.length) {
      parts.push({ text: value.slice(cursor), kind: "plain" });
    }
    return parts;
  }, [value, topicNames]);

  // The complete, known topic the caret is currently drilling into (`/foo.` →
  // `/foo`), or null when not in field mode or the topic isn't a known one.
  // Drives the lazy per-topic field fetch below.
  const activeTopicName = React.useMemo(() => {
    if (autocomplete === null) {
      return null;
    }
    const { topicPart, fieldFragment } = splitTopicQuery(autocomplete.query);
    if (fieldFragment === null) {
      return null;
    }
    const candidate = `/${topicPart}`;
    return topicNames.includes(candidate) ? candidate : null;
  }, [autocomplete, topicNames]);

  const topicFieldsQuery = useTopicFields(activeTopicName, orgId);
  // Stabilize the reference so the suggestions memo below doesn't recompute on
  // every render (a bare `?? []` allocates a fresh array each time).
  const topicFields = React.useMemo(
    () => topicFieldsQuery.data ?? [],
    [topicFieldsQuery.data],
  );

  const suggestions = React.useMemo<Suggestion[]>(() => {
    if (autocomplete === null) {
      return [];
    }
    const { topicPart, fieldFragment } = splitTopicQuery(autocomplete.query);
    if (fieldFragment === null) {
      const q = topicPart.toLowerCase();
      // Topic names already carry their leading `/`, which coincides with the
      // slash the user typed to open the menu — insert the name verbatim.
      return topicNames
        .filter((name) => name.toLowerCase().includes(q))
        .slice(0, MAX_SUGGESTIONS)
        .map((name) => ({ label: name, insertText: name, kind: "topic" }));
    }
    const q = fieldFragment.toLowerCase();
    // Field mode: suggest the active topic's own fields (fetched by name); the
    // user's leading `/` plus topicPart reconstruct the topic, and the chosen
    // field path is appended.
    return topicFields
      .filter((path) => path.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS)
      .map((path) => ({
        label: path,
        insertText: `/${topicPart}.${path}`,
        kind: "field",
      }));
  }, [autocomplete, topicNames, topicFields]);

  // Keep the highlight valid as the filtered list shrinks — the topic-fields
  // fetch resolves async, so the list can grow or shrink after the menu opens.
  React.useEffect(() => {
    clampHighlight(suggestions.length);
  }, [clampHighlight, suggestions.length]);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    refresh(
      event.target.value,
      event.target.selectionEnd ?? event.target.value.length,
    );
  }

  function handleSelect() {
    const ta = textareaRef.current;
    if (ta === null) {
      return;
    }
    refresh(ta.value, ta.selectionEnd ?? ta.value.length);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    handleNavKeyDown(event, suggestions.length, (index) =>
      accept(suggestions[index].insertText),
    );
  }

  const menuOpen = autocomplete !== null && suggestions.length > 0;
  const activeOptionId =
    menuOpen && autocomplete !== null
      ? optionId(autocomplete.highlight)
      : undefined;

  return (
    <div className={cx(styles.root, grow && styles.rootGrow)}>
      <label id={labelId} className={styles.label}>
        {`${label}${required ? " *" : ""}`}
      </label>
      <div
        className={cx(
          styles.field,
          focused && styles.fieldFocused,
          grow && styles.fieldGrow,
        )}
      >
        <MirrorHighlightTextarea
          value={value}
          overlay={segments.map((segment, idx) => {
            if (segment.kind === "topic") {
              return (
                <span key={idx} className={styles.topicToken}>
                  {segment.text}
                </span>
              );
            }
            if (segment.kind === "topicUnknown") {
              return (
                <span key={idx} className={styles.topicTokenUnknown}>
                  {segment.text}
                </span>
              );
            }
            if (segment.kind === "field") {
              return (
                <span key={idx} className={styles.fieldToken}>
                  {segment.text}
                </span>
              );
            }
            return <span key={idx}>{segment.text}</span>;
          })}
          textareaRef={textareaRef}
          overlayRef={overlayRef}
          onChange={handleChange}
          onSelect={handleSelect}
          onScroll={syncOverlayScroll}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          expanded={menuOpen}
          listboxId={listboxId}
          activeOptionId={activeOptionId}
          labelId={labelId}
          required={required}
          rows={minRows}
          textClassName={cx(styles.bodyText, inputClassName)}
          textareaClassName={grow ? styles.textareaGrow : styles.textareaResize}
        />
      </div>
      {helperText !== undefined && (
        <p className={styles.helperText}>{helperText}</p>
      )}
      <Popper
        open={menuOpen && anchorEl !== null}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        className={styles.popper}
      >
        <Paper ref={paperRef} elevation={4} className={styles.paper}>
          <MenuList dense id={listboxId} role="listbox">
            {suggestions.map((suggestion, idx) => (
              <MenuItem
                key={suggestion.insertText}
                id={optionId(idx)}
                role="option"
                aria-selected={idx === autocomplete?.highlight}
                selected={idx === autocomplete?.highlight}
                onClick={() => accept(suggestion.insertText)}
                onMouseEnter={() => setHighlight(idx)}
              >
                <span className={styles.suggestionLabel}>
                  {suggestion.label}
                </span>
                <span className={styles.suggestionKind}>{suggestion.kind}</span>
              </MenuItem>
            ))}
          </MenuList>
          <div className={styles.footer}>
            <Typography variant="caption" className={styles.hint}>
              ↑↓ to navigate · Enter or Tab to insert · Esc to dismiss
            </Typography>
          </div>
        </Paper>
      </Popper>
    </div>
  );
}
